import type { RunResponse } from '@malaberg/shared';
/**
 * Zustand store for the in-flight run state — T24.
 *
 * Holds all transient SSE-driven state for the current generation:
 * progress chips, streaming text, status, error, and the completed run.
 *
 * Kept separate from auth-store so it can be reset without touching auth.
 */
import { create } from 'zustand';

// ─── Types ────────────────────────────────────────────────────────────────────

export type RunStreamStatus = 'idle' | 'streaming' | 'done' | 'error';

export interface ProgressEvent {
  phase: string;
  message: string;
  at: string;
}

interface RunStreamState {
  runId: string | null;
  status: RunStreamStatus;
  progress: ProgressEvent[];
  deltaText: string;
  error: string | null;
  runResult: RunResponse | null;
  /**
   * Bumped whenever a run is created, completes, or errors. The RunsSidebar's
   * useRuns hook watches this counter so the list refetches automatically
   * (no manual Refresh click needed) when the user submits a new generation
   * or one finishes streaming.
   */
  runsListVersion: number;

  // Actions
  setRunId: (id: string | null) => void;
  /**
   * Called by GenerateButton on click — BEFORE the POST /api/runs returns.
   * Clears any prior run state and flips status to 'streaming' so the UI
   * immediately switches into the loading state instead of waiting for the
   * first SSE event (which can take several seconds with gpt-5.5 xhigh).
   */
  startSubmitting: () => void;
  appendProgress: (event: ProgressEvent) => void;
  appendDelta: (delta: string) => void;
  setDone: (run: RunResponse) => void;
  setError: (message: string, options?: { clearDeltaText?: boolean }) => void;
  bumpRunsListVersion: () => void;
  reset: () => void;
  /**
   * Loads a persisted run into the output pane — T25.
   * Called by RunCard when the user clicks a run in the sidebar.
   */
  loadRun: (run: RunResponse) => void;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState = {
  runId: null,
  status: 'idle' as RunStreamStatus,
  progress: [],
  deltaText: '',
  error: null,
  runResult: null,
  runsListVersion: 0,
};

// ─── Store ────────────────────────────────────────────────────────────────────

const FRIENDLY_INTERRUPTED_GENERATION =
  'Generation interrupted before completion. Partial draft was saved. Queued ads retry automatically; failed queue items can be requeued from Queue.';

const TRANSPORT_ERROR_PATTERNS = [
  'terminated',
  'fetch failed',
  'network error',
  'connection reset',
  'socket hang up',
  'body timeout',
  'stream error',
];

function mapPersistedRunError(error: string | null | undefined): string {
  if (!error) return 'Generation failed (no error message was recorded).';

  const normalized = error.trim().toLowerCase();
  if (!normalized) return 'Generation failed (no error message was recorded).';

  if (TRANSPORT_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern))) {
    return FRIENDLY_INTERRUPTED_GENERATION;
  }

  return error;
}

export const useRunStreamStore = create<RunStreamState>((set) => ({
  ...initialState,

  // Setting a real runId means a generation is in flight. Flip status to
  // 'streaming' immediately so the UI shows the loading state before the
  // first SSE event arrives (gpt-5.5 xhigh has a multi-minute warm-up).
  // Also bump runsListVersion — POST /api/runs has just returned with the
  // new run id, so the new row is now in the DB and the sidebar refetch
  // will pick it up.
  setRunId: (id) =>
    set((s) =>
      id
        ? {
            runId: id,
            status: 'streaming',
            runsListVersion: s.runsListVersion + 1,
          }
        : { runId: id }
    ),

  // Called on Generate click BEFORE the POST returns. Flips into the
  // loading state so the user sees feedback instantly. Doesn't bump the
  // sidebar counter — the new run isn't in the DB yet; setRunId will
  // bump once the POST resolves.
  startSubmitting: () =>
    set({
      runId: null,
      status: 'streaming',
      progress: [],
      deltaText: '',
      error: null,
      runResult: null,
    }),

  appendProgress: (event) =>
    set((state) => ({
      status: 'streaming',
      progress: [...state.progress, event],
    })),

  appendDelta: (delta) =>
    set((state) => ({
      status: 'streaming',
      deltaText: state.deltaText + delta,
    })),

  setDone: (run) =>
    set((s) => ({
      status: 'done',
      runResult: run,
      deltaText: run.outputMarkdown ?? s.deltaText,
      // Run finished — refresh the sidebar so the card now reflects cost,
      // status badge "Done", final timestamps, etc.
      runsListVersion: s.runsListVersion + 1,
    })),

  setError: (message, options) =>
    set((s) => ({
      status: 'error',
      error: message,
      ...(options?.clearDeltaText ? { deltaText: '' } : {}),
      runsListVersion: s.runsListVersion + 1,
    })),

  bumpRunsListVersion: () =>
    set((s) => ({
      runsListVersion: s.runsListVersion + 1,
    })),

  // Preserve the runsListVersion across reset so a subscriber's refetch
  // counter doesn't tick backward.
  reset: () => set((s) => ({ ...initialState, runsListVersion: s.runsListVersion })),

  loadRun: (run) => {
    // Map the persisted run status to a stream status.
    // - draft / running   → still in flight; subscribe to SSE so live deltas
    //   stream in and the UI shows the loading state. Setting runId to the
    //   loaded run's id triggers useRunStream to open /api/runs/:id/events.
    // - failed            → surface the error text in the output pane.
    // - completed / edited / approved / logged → terminal; show the saved
    //   markdown. Clearing runId ensures any prior SSE connection closes.
    const isInFlight = run.status === 'draft' || run.status === 'running';
    const isFailed = run.status === 'failed';

    if (isInFlight) {
      set({
        runId: run.id,
        status: 'streaming',
        deltaText: run.outputMarkdown ?? '',
        runResult: run,
        progress: [],
        error: null,
      });
      return;
    }

    set({
      runId: null,
      deltaText: run.outputMarkdown ?? '',
      status: isFailed ? 'error' : 'done',
      runResult: run,
      progress: [],
      error: isFailed ? mapPersistedRunError(run.error) : null,
    });
  },
}));
